import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FileSearch, Calculator, FileOutput } from "lucide-react";
import ChapterShell from "@/tutorial/ChapterShell";
import { Section } from "@/tutorial/components/TutorialComponents";

const FAQ = {
  pdf: {
    icon: FileSearch,
    label: "Import & Extraction PDF",
    items: [
      {
        q: "Mon PDF ne s'extrait pas correctement. Que faire ?",
        a: "Vérifiez d'abord le score de fiabilité (chapitre 2.3). Si le score est bas, votre PDF est probablement scanné : passez-le par un OCR (Adobe Acrobat, Tesseract) avant ré-upload. Sinon, utilisez la revue éditable pour corriger manuellement les chiffres clés.",
      },
      {
        q: "Le score de fiabilité est à 45 %. Puis-je quand même analyser ?",
        a: "Oui mais pas en l'état : corrigez d'abord les 5 chiffres clés (recettes, dépenses, déficit, dette, taux d'imposition) dans la revue éditable. Une fois ces valeurs fiabilisées manuellement, l'analyse vitaliste est juste, peu importe le score d'extraction initial.",
      },
      {
        q: "L'extraction a pris plus de 2 minutes et n'a pas fini.",
        a: "Les très gros PDF (> 50 Mo) prennent du temps. L'analyse continue en arrière-plan même si l'écran semble figé : rafraîchissez la page après 3 minutes — votre document apparaîtra en statut « Analysé » dans la liste.",
      },
      {
        q: "Les chiffres extraits sont en langue arabe. Est-ce supporté ?",
        a: "L'extraction texte reconnaît les chiffres arabes occidentaux (٠١٢٣…). Pour les PDF rédigés en arabe avec mise en page RTL, l'extraction reste fiable mais la revue manuelle est recommandée. La traduction de l'interface en arabe est prévue dans une prochaine version.",
      },
    ],
  },
  calc: {
    icon: Calculator,
    label: "Calculs vitalistes",
    items: [
      {
        q: "Mes graphiques affichent des zéros. Pourquoi ?",
        a: "Trois causes possibles : (1) aucun document n'a été analysé, (2) le profil contribuable n'a pas été défini (étape 2/5 du wizard), (3) les chiffres clés extraits valent 0 (PDF mal lu). Vérifiez ces trois points dans l'ordre.",
      },
      {
        q: "Le résultat « jours de vie » semble trop élevé. Comment vérifier ?",
        a: "Reprenez la formule : Jours = Total prélèvements ÷ Salaire journalier. Si le salaire mensuel est sous-estimé (par exemple 50 UM au lieu de 500), le résultat explose. Ouvrez l'étape « Profil » du wizard et corrigez le salaire de référence.",
      },
      {
        q: "Puis-je comparer deux pays différents ?",
        a: "Oui : créez un projet par pays, ouvrez « Mes analyses », cochez les deux projets et cliquez sur Comparer. La comparaison est normalisée en jours de vie, ce qui rend les pays comparables même avec des devises et des PIB différents.",
      },
      {
        q: "La dette intergénérationnelle n'est pas calculée. Pourquoi ?",
        a: "Ce module nécessite que la dette publique nationale figure dans les données extraites (encours total + charge annuelle). Vérifiez l'étape de validation : si « Dette publique » est vide, l'analyse intergénérationnelle est désactivée.",
      },
    ],
  },
  reports: {
    icon: FileOutput,
    label: "Rapports & fonctionnalités",
    items: [
      {
        q: "Le bouton « Simuler le jugement » est grisé.",
        a: "Le bouton se déverrouille uniquement quand les 4 conditions du chapitre 7.1 sont remplies (analyse budgétaire, juridique, au moins 1 texte légal, avertissement confirmé). Vérifiez ces 4 cases.",
      },
      {
        q: "Mon rapport PDF ne contient pas les graphiques.",
        a: "La capture des graphiques peut échouer si la page n'a pas fini de rendre. Re-générez le rapport après avoir ouvert le dashboard de l'analyse pendant 3 secondes pour laisser charger les visualisations Recharts.",
      },
      {
        q: "Je ne trouve plus un rapport généré il y a 3 jours.",
        a: "Les rapports sont stockés côté navigateur (localStorage + IndexedDB). Si vous avez vidé le cache ou changé de navigateur, ils sont perdus. Pensez à télécharger les fichiers générés et à archiver les liens de partage côté serveur (valides 7 jours).",
      },
      {
        q: "Puis-je utiliser l'application en arabe ?",
        a: "L'infrastructure i18n est en place (FR canonique aujourd'hui). Les versions anglaise et arabe — y compris RTL — figurent au backlog officiel et seront livrées dans un cycle dédié.",
      },
      {
        q: "Comment partager mon analyse avec un collègue ?",
        a: "Depuis « Mes analyses » → bouton Partager → un lien sécurisé est généré, valable 7 jours par défaut, révocable à tout moment. Le destinataire n'a pas besoin de compte VITAE-PUBLICA™.",
      },
      {
        q: "Mes données sont-elles confidentielles ?",
        a: "Vos PDF et analyses sont stockés sur votre projet personnel et accessibles uniquement après authentification JWT. Aucune donnée n'est envoyée à un tiers en dehors d'un appel ponctuel à GPT-4o pour l'extraction structurée — sans nom propre ni identifiants personnels.",
      },
      {
        q: "Comment contacter le support technique ?",
        a: "Utilisez le formulaire de contact public depuis la page « À propos » accessible sans connexion. Précisez votre email, votre organisation et l'identifiant éventuel de votre projet pour un retour rapide.",
      },
    ],
  },
};

export default function Chapter10() {
  return (
    <ChapterShell id={10}>
      <Section
        id="section-10-0"
        eyebrow="10.0"
        title="Foire aux questions et dépannage"
      >
        <p>
          Ce chapitre regroupe les quinze questions les plus fréquemment
          posées, organisées en trois catégories. Cliquez sur une question
          pour déplier la réponse.
        </p>
      </Section>

      {Object.entries(FAQ).map(([key, cat]) => {
        const Icon = cat.icon;
        return (
          <Section
            key={key}
            id={`section-10-${key}`}
            eyebrow={`Catégorie · ${cat.label}`}
            title={cat.label}
          >
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon className="h-4 w-4 text-brand-gold" />
              {cat.items.length} questions
            </div>
            <Accordion
              type="single"
              collapsible
              className="rounded-lg border border-border bg-card"
              data-testid={`chapter-10-accordion-${key}`}
            >
              {cat.items.map((item, i) => (
                <AccordionItem
                  key={`${key}-${i}`}
                  value={`${key}-${i}`}
                  data-testid={`chapter-10-faq-${key}-${i}`}
                >
                  <AccordionTrigger className="px-4 text-left text-sm font-semibold">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-4 text-sm leading-relaxed text-muted-foreground">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Section>
        );
      })}

      <Section
        id="section-10-about"
        eyebrow="À propos"
        title="VITAE-PUBLICA™"
      >
        <div className="rounded-xl border border-brand-gold/40 bg-brand-gold/5 p-5">
          <p className="text-sm leading-relaxed">
            VITAE-PUBLICA™ est fondé sur la <strong>Nouvelle Théorie des
            Finances Publiques</strong> d'Ahmed ELY Mustapha — qui pose la vie
            humaine comme unité de compte de toute politique budgétaire.
          </p>
          <p className="mt-3 text-sm leading-relaxed">
            Programmeur :{" "}
            <strong>ELY Mustapha</strong> — PMP · I-PMP · IBM Full Stack Developer.
          </p>
          <p className="mt-3 text-xs italic text-muted-foreground">
            Document pédagogique sans valeur juridictionnelle.
          </p>
        </div>
      </Section>
    </ChapterShell>
  );
}
